import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthState } from '@/hooks/useAuth';
import { useUserData } from '@/hooks/useUserData';
import { updateUserThemePreference } from '@/lib/firebase';
import { syncNativeStatusBarForTheme } from '@/lib/nativeStatusBar';
import { UPDATE_RELOAD_PENDING_CLASS } from '@/lib/updateReloadSplash';

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  currentThemeMode: ThemeMode;
  toggleThemeMode: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = 'getmoretats-theme-mode';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuthState();
  const { userData, loading: userDataLoading } = useUserData();
  
  const [currentThemeMode, setCurrentThemeMode] = useState<ThemeMode>(() => {
    // Check localStorage first for immediate theme application
    const storedThemeMode = localStorage.getItem(THEME_STORAGE_KEY);
    if (storedThemeMode === 'light' || storedThemeMode === 'dark') {
      return storedThemeMode;
    }
    
    // Default to dark mode
    return 'dark';
  });

  // Update theme when user data loads
  useEffect(() => {
    if (!userDataLoading && userData?.theme) {
      // If user has a theme preference in Firestore, use that
      if (userData.theme !== currentThemeMode) {
        setCurrentThemeMode(userData.theme);
        // IMPORTANT: Also update localStorage to keep it in sync with Firestore
        localStorage.setItem(THEME_STORAGE_KEY, userData.theme);
      }
    }
  }, [userData?.theme, userDataLoading, currentThemeMode]);

  // Apply theme to document
  useEffect(() => {
    const documentElement = document.documentElement;
    
    const updateReloadPending = documentElement.classList.contains(UPDATE_RELOAD_PENDING_CLASS);

    if (currentThemeMode === 'dark' || updateReloadPending) {
      documentElement.classList.add('dark');
    } else {
      documentElement.classList.remove('dark');
    }
    
    // Update theme-color meta tag for PWA status bar
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', currentThemeMode === 'dark' ? '#262626' : '#ffffff');
    }
    
    // Update iOS status bar style
    const statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (statusBarMeta) {
      statusBarMeta.setAttribute('content', currentThemeMode === 'dark' ? 'black-translucent' : 'default');
    }

    void syncNativeStatusBarForTheme(currentThemeMode);
  }, [currentThemeMode]);

  const saveThemePreference = async (mode: ThemeMode) => {
    // Save to localStorage for immediate persistence
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    
    // Save to Firestore if user is authenticated
    if (user?.uid) {
      try {
        await updateUserThemePreference(user.uid, mode);
      } catch (error) {
        console.error('Failed to save theme preference to Firestore:', error);
        // Continue with localStorage fallback
      }
    }
  };

  const toggleThemeMode = () => {
    const newMode = currentThemeMode === 'light' ? 'dark' : 'light';
    setCurrentThemeMode(newMode);
    saveThemePreference(newMode);
  };

  const setThemeMode = (mode: ThemeMode) => {
    setCurrentThemeMode(mode);
    saveThemePreference(mode);
  };

  const contextValue: ThemeContextValue = {
    currentThemeMode,
    toggleThemeMode,
    setThemeMode,
    isDarkMode: currentThemeMode === 'dark',
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
